import * as React from 'react';
import type { LucideIcon } from 'lucide-react';
import { Card, CardContent, CardFooter, CardHeader } from '../ui/card';
import { cn } from '../../lib/utils';

export interface DashboardCardProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Card title. */
  title?: React.ReactNode;
  /** Subtitle / one-line description. */
  description?: React.ReactNode;
  /** Optional Lucide icon shown in the title row. */
  icon?: LucideIcon;
  /** Right-aligned actions in the header row (filter, refresh, "View all"). */
  headerActions?: React.ReactNode;
  /** Footer slot (links, summaries, secondary metrics). */
  footer?: React.ReactNode;
  /** When true, the card has no inner padding — useful for tables / charts. */
  flushBody?: boolean;
}

/**
 * DashboardCard — the canonical dashboard tile wrapper.
 *
 * A composable shape: title row with optional icon + actions, content body,
 * optional footer. Pairs with MetricCard / ActivityPanel / TableShell when
 * the body should be more opinionated.
 */
export const DashboardCard = React.forwardRef<
  HTMLDivElement,
  DashboardCardProps
>(
  (
    {
      title,
      description,
      icon: Icon,
      headerActions,
      footer,
      flushBody,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const hasHeader = Boolean(title || description || headerActions || Icon);
    return (
      <Card ref={ref} className={cn('flex flex-col', className)} {...props}>
        {hasHeader && (
          <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0 pb-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                {Icon && (
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-muted text-muted-foreground">
                    <Icon className="h-4 w-4" />
                  </span>
                )}
                {title && (
                  <h3 className="truncate text-sm font-semibold tracking-tight text-foreground">
                    {title}
                  </h3>
                )}
              </div>
              {description && (
                <p className="mt-1 text-xs text-muted-foreground">
                  {description}
                </p>
              )}
            </div>
            {headerActions && (
              <div className="flex shrink-0 items-center gap-1.5">
                {headerActions}
              </div>
            )}
          </CardHeader>
        )}
        <CardContent
          className={cn(
            'flex-1',
            flushBody && 'p-0',
            !hasHeader && 'pt-5',
          )}
        >
          {children}
        </CardContent>
        {footer && (
          <CardFooter className="border-t border-border p-4 text-xs text-muted-foreground">
            {footer}
          </CardFooter>
        )}
      </Card>
    );
  },
);
DashboardCard.displayName = 'DashboardCard';
